import { useContext, useEffect } from "react";
import { ApplicationState } from "../../services/context/ApplicationStateContext";
import useUnreadCount from "../../services/utils/custom hooks/Notifications/useUnreadCount";
import useOrganizations from "../../services/utils/custom hooks/useOrganizations";
import usePathLabs from "../../services/utils/custom hooks/usePathLabs";
import usePhysiciansOfOrg from "../../services/utils/custom hooks/usePhysiciansOfOrg";
import ContextSelector from "./ContextSelector";

function Selectors({
  profile,
  className,
}: {
  profile: any;
  className: string;
}) {
  const {
    state: { selectedOrganization, selectedPhysician, selectedPathLab },
    dispatch,
  } = useContext(ApplicationState);
  const { isLoading: fetchingNotificationCount } = useUnreadCount();
  const { data: orgs, isLoading } = useOrganizations();
  const { data: pathLabs, isLoading: fetchingPathLabs } = usePathLabs();
  const { data: physicians, isLoading: fetchingPhysicians } =
    usePhysiciansOfOrg();
  const orgsList = orgs?.data
    ? orgs?.data?.map((org) => ({
        label: org.name,
        value: org.id,
        image: org.logoUrl,
        address: org.address,
        state: org.state,
        zipcode: org.zipcode,
      }))
    : [];
  const physiciansList = physicians?.data
    ? physicians?.data?.map((physician) => ({
        label: `${physician?.firstName} ${physician.lastName}`,
        value: physician?.id,
        image: physician?.imageUrl,
      }))
    : [];
  const pathLabsList = pathLabs?.data
    ? pathLabs?.data?.map((org) => ({
        label: org.name,
        value: org.id,
        image: org.logoUrl,
        address: org.address,
        state: org.state,
        zipcode: org.zipcode,
      }))
    : [];
  const roles = profile?.user?.roles ?? [];
  const isCOA = roles.includes("Collaborative Order Agreement");
  const isOnlyPLU = roles?.includes("PathLab User") && roles.length === 1;

  function handleOrganizationClick(value: string | number) {
    const selectedOrg = orgs?.data?.find((org: any) => org.id === value); // Adjust based on the actual structure

    dispatch({ type: "SET_ORGANIZATION", payload: value });

    if (selectedOrg) {
      dispatch({ type: "SET_ORGANIZATION_DATA", payload: selectedOrg });
    } else {
      console.error("Organization not found for ID:", value);
    }

    if (
      roles.includes("Office Manager") ||
      roles.includes("Collaborative Order Agreement")
    ) {
      dispatch({ type: "SET_PHYSICIAN", payload: "" });
      dispatch({ type: "SET_PHYSICIAN_DATA", payload: null });
    }
  }

  function handlePathLabClick(value: string | number) {
    dispatch({ type: "SET_PATHLAB", payload: value });
  }

  useEffect(() => {
    if (roles?.includes("Physician") && roles?.length == 1) {
      dispatch({ type: "SET_PHYSICIAN", payload: profile.user.id });
      dispatch({ type: "SET_PHYSICIAN_DATA", payload: profile?.user });
    }
  }, [roles]);

  return (
    <div className={`items-center lg:gap-2 gap-1 ${className}`}>
      {roles && (isCOA || roles?.includes("PathLab User")) && (
        <ContextSelector
          disabled={isLoading || pathLabs?.data?.length === 0}
          handleOptionClick={handlePathLabClick}
          value={selectedPathLab ?? ""}
          tooltipId="PathLabTooltip"
          tooltipText="Please select PathLab"
          defaultLabel="All PathLabs"
          isCOA={isCOA}
          options={[
            { label: "All PathLabs", value: "", image: "" },
            ...pathLabsList,
          ]}
          isLoading={isLoading || fetchingNotificationCount || fetchingPathLabs}
        />
      )}
      {roles && !isOnlyPLU && (
        <ContextSelector
          disabled={isLoading || orgs?.data?.length === 0}
          handleOptionClick={handleOrganizationClick}
          value={selectedOrganization ?? ""}
          tooltipId="OrganizationTooltip"
          tooltipText="Please select Organization"
          defaultLabel="All Organizations"
          isCOA={isCOA}
          options={[
            {
              label: "All Organizations",
              value: "",
              image: "",
              address: "",
              state: "",
              zipcode: "",
            },
            ...orgsList,
          ]}
          isLoading={isLoading || fetchingNotificationCount}
        />
      )}
      {(roles?.includes("Office Manager") ||
        roles?.includes("Collaborative Order Agreement")) && (
        <ContextSelector
          disabled={
            fetchingPhysicians || isLoading || !!selectedOrganization === false
          }
          defaultLabel="All Clinicians"
          tooltipId="PhysicianTooltip"
          tooltipText="Please select an Physician"
          handleOptionClick={(value) => {
            dispatch({ type: "SET_PHYSICIAN", payload: value });
            // find full physician object from fetched data and store it
            const selectedPhysicianObj = physicians?.data?.find(
              (p: any) => String(p.id) === String(value)
            );
            dispatch({ type: "SET_PHYSICIAN_DATA", payload: selectedPhysicianObj ?? null });
          }}
          isCOA={isCOA}
          value={selectedPhysician ?? ""}
          options={[
            { label: "All Clinicians", value: "", image: "" },
            ...physiciansList,
          ]}
          isLoading={
            isLoading || fetchingPhysicians || fetchingNotificationCount
          }
        />
      )}
    </div>
  );
}

export default Selectors;
